import React, { useEffect, useMemo, useState } from 'react';
import { Field, Form, Formik, FormikErrors } from 'formik';
import { v4 as generateUUID } from 'uuid';
import { PDFDocument } from 'pdf-lib';
import { DateTime } from 'luxon';

import { MiniAppFocusWrapper } from '../miniapp-focus-wrapper/miniapp-focus-wrapper';
import { IdentityTypeDirectors, InputField, ListItem, stripWhitespace } from 'src/features/common';
import {
  Bank,
  bankInfo,
} from 'src/features/onboarding/pages/bank-details/resources/bank-details-resources';
import {
  isValidID,
  isValidName,
  isValidNumbers,
  isValidRegistrationNumber,
} from 'src/helpers/validation';
import RevampFileUpload, { AcceptedFileTypes } from '../revamp-file-upload/revamp-file-upload';
import BankingDetailsOTP from '../banking-details-otp/banking-details-otp';
import FullPageLoader from '../full-page-loader/full-page-loader';
import UpdateInProgress from '../update-in-progress/update-in-progress';
import { InfoImage } from 'src/assets/images';
import { BankAccountTypes } from 'src/helpers/constants';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import {
  BankDetailsUpdate,
  updateMerchantBankDetails,
} from 'src/services/merchant-profile.service';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { mergeJpegFiles, mergePdfFiles } from 'src/helpers/pdf';
import { RadioButton } from '../radio-button/radio-button';
import { RadioButtonGroup } from '../radio-button-group/radio-button-group';
import {
  asylumMask,
  businessRegistrationMask,
  idMask,
  passportMask,
  refugeeMask,
  spaceBy4Mask,
} from 'src/helpers/formatting';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { IDENTITY_TYPE_LIST } from 'src/features/onboarding/pages/personal-details/personal-details';
import ShowIf from '../show-if/show-if';
import TealiumEvents from 'src/helpers/tealium-events';
import { Environment } from 'src/Environment';
import { IdType } from 'src/features/common/types/bank-details-validation';
import { BankAccountValidationService } from 'src/features/common/services/bank-account-validation-service';

type BankingDetailsFormErrors = {
  accountType: string;
  bankName: string;
  accountHolder: string;
  accountNumber: string;
  proofOfBanking: string[];
  idNumber: string;
  businessRegistrationNumber: string;
};

type Option = {
  value: string;
  label: string;
};

enum ACCOUNT_HOLDER_TYPE {
  PERSONAL = 'personal',
  BUSINESS = 'business',
}

type BankingDetailsValues = {
  accountType: Option;
  bankName: Bank;
  accountHolder: string;
  idNumber?: string;
  businessRegistrationNumber?: string;
  accountNumber: string;
  proofOfBanking: string[];
  accountHolderType?: ACCOUNT_HOLDER_TYPE;
  identityType: ListItem<IdentityTypeDirectors>;
};

type BankingDetailsFormProps = {
  isBankDetailsLoading: boolean;
  pendingUpdate: boolean;
  getMerchantDetails: (abortController: AbortController, merchantId: string) => void;
};

const bankAccountValidation = new BankAccountValidationService(
  Environment.current.apiHost,
  Environment.current.newApiBase,
);

function BankingDetailsForm({
  isBankDetailsLoading,
  pendingUpdate,
  getMerchantDetails,
}: BankingDetailsFormProps) {
  const MAX_BAV_ATTEMPTS = 3;
  const BAVAttemptsCacheKey = 'bakAccountValidationAttempts';

  const { merchantBankDetails, setMerchantBankDetails, merchant } = useMerchantProfile();
  const [changeBankDetails, setChangeBankDetails] = useState<boolean>(false);
  const [bankDetailsValid, setBankDetailsValid] = useState<boolean>(false);
  const [showUpdateInProgress, setShowUpdateInProgress] = useState<boolean>(false);
  const [showOtp, setShowOtp] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<BankingDetailsValues>();
  const [fileTypes, setFileTypes] = useState<string[]>([]);
  const [BAVAttempts, setBAVAttempts] = useState<number>(
    Number(sessionStorage.getItem(BAVAttemptsCacheKey)) || 0,
  );

  // Traderoot account type mapping
  const accountTypeMapping = {
    0: 'CURRENT_CHEQUE',
    1: 'SAVINGS',
  };

  const accountTypeMapping2 = {
    CURRENT_CHEQUE: 0,
    SAVINGS: 1,
  };

  const bavAccountTypeMap = new Map<string, number>([
    ['CURRENT_CHEQUE', 1],
    ['SAVINGS', 2],
    ['TRANSMISSION', 3],
  ]);

  const inMiniApp = useMemo((): boolean => MiniAppEvents.isInMiniApp(), []);

  const bankName = useMemo(() => {
    if (pendingUpdate) {
      return null;
    }
    return bankInfo.bankList.find(
      (item) =>
        item.label === merchantBankDetails?.bankName ||
        item.salesforceValue === merchantBankDetails?.bankName,
    );
  }, [merchantBankDetails]);

  const accType = useMemo(() => {
    if (pendingUpdate) {
      return { value: 'Pending', label: 'Pending' };
    }
    return BankAccountTypes.find(
      (item) => item.value === accountTypeMapping[merchantBankDetails.bankAccountType],
    );
  }, [merchantBankDetails]);

  const changeBankDetailsDisabled: boolean = useMemo(
    () => Environment.current.disabledFeatures.changeBankDetails,
    [],
  );

  const bavDisabled: boolean = useMemo(() => Environment.current.disabledFeatures.bav, []);

  useEffect(() => {
    setBankDetailsValid(false);
  }, [changeBankDetails]);

  useEffect(() => {
    if (pendingUpdate) {
      setShowUpdateInProgress(true);
    }
  }, [pendingUpdate]);

  const mergeDocuments = async (bankDocs: string[], isJpeg: boolean) => {
    try {
      const mergedDoc = isJpeg ? await mergeJpegFiles(bankDocs) : await mergePdfFiles(bankDocs);
      const pdfDoc = await PDFDocument.load(mergedDoc, { ignoreEncryption: true });
      return await pdfDoc.saveAsBase64();
    } catch (error) {
      console.error(error);
    }
  };

  const checkIdType = (values: BankingDetailsValues) => {
    switch (values.accountHolderType) {
      case ACCOUNT_HOLDER_TYPE.PERSONAL:
        return values.identityType.value === IdentityTypeDirectors.RSA_ID
          ? IdType.SouthAfricanID
          : IdType.ForeignPassport;
      case ACCOUNT_HOLDER_TYPE.BUSINESS:
        return IdType.SouthAfricanBusiness;
      default:
        return IdType.SouthAfricanID;
    }
  };

  const checkBankDetailsValid = async (values: BankingDetailsValues) => {
    const abortController = new AbortController();
    const requestTime = new Date().toISOString();
    const idCompRegNo =
      values.accountHolderType === ACCOUNT_HOLDER_TYPE.PERSONAL
        ? stripWhitespace(values.idNumber)
        : values.businessRegistrationNumber;
    const surnameCompName = values.accountHolder;

    return await bankAccountValidation.onlineBankAccountValidation(
      {
        requestId: generateUUID(),
        requestTime,
        merchantNumber: merchant.merchantId,
        bavAccountType: bavAccountTypeMap.get(values.accountType.value),
        bankName: values.bankName.salesforceValue,
        branchCode: values.bankName.universalBranchCode,
        idCompRegNo,
        idType: checkIdType(values),
        surnameCompName,
        accountNo: values.accountNumber,
      },
      abortController,
    );
  };

  const getIdPlaceholder = (identityType: IdentityTypeDirectors | undefined) => {
    switch (identityType) {
      case IdentityTypeDirectors.RSA_ID:
        return 'RSA ID Number';

      case IdentityTypeDirectors.PASSPORT:
        return 'Passport number';

      case IdentityTypeDirectors.REFUGEE:
      case IdentityTypeDirectors.ASYLUM_SEEKER:
      default:
        return 'Document ID';
    }
  };

  const getIdMask = (values: BankingDetailsValues) => {
    switch (values.identityType?.value) {
      case IdentityTypeDirectors.RSA_ID:
        return idMask;
      case IdentityTypeDirectors.PASSPORT:
        return passportMask;
      case IdentityTypeDirectors.ASYLUM_SEEKER:
        return asylumMask;
      case IdentityTypeDirectors.REFUGEE:
        return refugeeMask;
      default:
        return spaceBy4Mask;
    }
  };

  const validate = (values: BankingDetailsValues) => {
    const errors: FormikErrors<BankingDetailsFormErrors> = {};

    if (!values.accountType) {
      errors.accountType = 'Please select an account type';
    }

    if (!values.bankName) {
      errors.bankName = 'Please select a bank name';
    }

    if (!values.accountHolder || !isValidName(stripWhitespace(values.accountHolder))) {
      errors.accountHolder = 'The account holder entered is incorrect';
    }

    if (!values.accountNumber) {
      errors.accountNumber = 'Please enter an account number';
    }

    if (
      !isValidNumbers(values.accountNumber) ||
      values.accountNumber.length < 2 ||
      values.accountNumber.length > 16
    ) {
      errors.accountNumber = 'Invalid account number';
    }

    if (
      values.accountHolderType === ACCOUNT_HOLDER_TYPE.PERSONAL &&
      values.identityType.value === IdentityTypeDirectors.RSA_ID &&
      !isValidID(stripWhitespace(values.idNumber))
    ) {
      errors.idNumber = 'Invalid Id number';
    }

    if (
      values.accountHolderType === ACCOUNT_HOLDER_TYPE.BUSINESS &&
      values.businessRegistrationNumber &&
      !isValidRegistrationNumber(values.businessRegistrationNumber)
    ) {
      errors.businessRegistrationNumber = 'Invalid business registration number';
    }

    if (
      (!values.proofOfBanking ||
        !Array.isArray(values.proofOfBanking) ||
        values.proofOfBanking.length === 0) &&
      bavDisabled
    ) {
      errors.proofOfBanking = 'Proof is incorrect';
    }

    setBankDetailsValid(Object.values(errors).length === 0);
    return errors;
  };

  const submit = async (values: BankingDetailsValues) => {
    setShowLoader(true);
    setFormValues(values);

    const isBankDetailsValid = await checkBankDetailsValid(values);
    let errorMessage = isBankDetailsValid.message;

    if (!isBankDetailsValid.ok) {
      if (!errorMessage) {
        const newBAVAttempts = BAVAttempts >= MAX_BAV_ATTEMPTS ? MAX_BAV_ATTEMPTS : BAVAttempts + 1;
        setBAVAttempts(newBAVAttempts);
        sessionStorage.setItem(BAVAttemptsCacheKey, String(newBAVAttempts));

        switch (newBAVAttempts) {
          case MAX_BAV_ATTEMPTS - 1:
            errorMessage = 'Invalid bank details. Please try again. You have one attempt left.';
            break;
          case MAX_BAV_ATTEMPTS:
            errorMessage = 'Invalid bank details. Maximum attempts reached.';
            break;
          default:
            errorMessage = 'Invalid bank details. Please try again.';
            break;
        }
      }

      setShowLoader(false);
      toastMessageServiceSingleton.addError(errorMessage, null, '', false, null, true);
      return;
    }

    const abortController = new AbortController();

    const requestId = generateUUID();
    setMerchantBankDetails({ ...merchantBankDetails, requestId });

    const isJpeg = fileTypes.every((type) => type === AcceptedFileTypes.jpeg);
    const bankDocument =
      values.proofOfBanking.length > 1
        ? await mergeDocuments(values.proofOfBanking, isJpeg)
        : values.proofOfBanking[0];

    if (!bankDocument && bavDisabled) {
      toastMessageServiceSingleton.addError(
        'Could not upload document(s). Please try again.',
        null,
        '',
        false,
        null,
        true,
      );
      setShowLoader(false);
      setChangeBankDetails((prev) => !prev);
      return;
    }

    const data: BankDetailsUpdate = {
      requestId: requestId,
      requestedDateTime: DateTime.now().toString(),
      accountHolderName: values.accountHolder,
      bankName: values.bankName.salesforceValue,
      branchName: values.bankName.universalBranchName,
      branchCode: values.bankName.universalBranchCode,
      accountNumber: values.accountNumber,
      accountType: `${accountTypeMapping2[values.accountType.value]}`,
      email: merchantBankDetails.emailAddress,
      msisdn: merchantBankDetails.mobilePhoneNumber,
      ...(bavDisabled && {
        mimeType: isJpeg ? AcceptedFileTypes.jpeg : AcceptedFileTypes.pdf,
        bankDocument,
      }),
    };

    if (!merchant.merchantId) {
      setShowLoader(false);
      setChangeBankDetails((prev) => !prev);
      return;
    }

    try {
      const result = await updateMerchantBankDetails(abortController, data, merchant.merchantId);

      if (!result.ok) {
        toastMessageServiceSingleton.addError(result.message, null, '', false, null, true);
        setShowLoader(false);
        setChangeBankDetails((prev) => !prev);
        return;
      }

      setShowLoader(false);
      setShowOtp(true);
      setChangeBankDetails((prev) => !prev);
    } catch (error) {
      setShowLoader(false);
      setChangeBankDetails((prev) => !prev);
      console.error(error);
    }
  };

  const onClick = () => {
    TealiumEvents.changeBankingDetailsButton();
    if (!changeBankDetails && pendingUpdate) {
      setShowUpdateInProgress(true);
      return;
    }
    setChangeBankDetails((prev) => !prev);
    if (changeBankDetails) {
      setShowOtp(false);
    }
  };

  const initialValues = () => {
    const values: BankingDetailsValues = {
      accountType: accType ?? { value: '', label: '' },
      bankName: bankName!,
      accountHolder: merchantBankDetails?.bankAccountHolderName ?? '',
      idNumber: '',
      businessRegistrationNumber: merchant.companyRegistrationNumber ?? '',
      accountNumber: merchantBankDetails?.bankAccountNumber ?? '',
      proofOfBanking: [],
      accountHolderType: ACCOUNT_HOLDER_TYPE.PERSONAL,
      identityType: { value: IdentityTypeDirectors.RSA_ID, label: 'South African' },
    };

    return values;
  };

  const requestNewOTP = () => {
    if (formValues) {
      submit(formValues);
    }
  };

  const handleExit = async () => {
    setShowOtp(false);
    const abortController = new AbortController();
    if (!merchant?.merchantId) return;
    await getMerchantDetails(abortController, merchant?.merchantId);
  };

  return (
    <>
      <Formik<BankingDetailsValues>
        initialValues={initialValues()}
        validate={validate}
        onSubmit={submit}
        validateOnChange
        validateOnBlur
        validateOnMount
        enableReinitialize
      >
        {({ values }) => (
          <Form className="flex flex-col w-full max-w-lg gap-y-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <img className="w-5 h-5" src={InfoImage} alt="info" />
              <p className="text-grey-100">This is where your transactions will be paid to.</p>
            </div>
            {changeBankDetails ? (
              <>
                <div className="px-4">
                  <RadioButtonGroup automationId="Merchant_Update_banking_details_Radio_Button_Group_Account_Type">
                    <Field
                      type="radio"
                      name="accountHolderType"
                      value={bankInfo.accountHolderType.PERSONAL}
                      label="Personal account"
                      component={RadioButton}
                      automationId="Onboarding_Create_Account_Your_Details_Radio_Button_Group_Account_Type_Value_Personal"
                    />
                    <Field
                      type="radio"
                      name="accountHolderType"
                      value={bankInfo.accountHolderType.BUSINESS}
                      label="Business account"
                      component={RadioButton}
                      automationId="Onboarding_Create_Account_Your_Details_Radio_Button_Group_Account_Type_Value_Business"
                    />
                  </RadioButtonGroup>
                </div>

                <Field
                  name="accountType"
                  type="text"
                  items={BankAccountTypes}
                  getItemDisplayValue={(item: Option) => item.label}
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  className="dark"
                  fieldType="select"
                  placeholder="Account Type"
                  isDark
                />
                <Field
                  name="bankName"
                  type="text"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  items={bankInfo.bankList}
                  getItemDisplayValue={(item: Bank) => item.label}
                  className="dark"
                  fieldType="select"
                  placeholder="Bank Name"
                  isDark
                />
                <Field
                  name="accountHolder"
                  type="text"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  className="dark"
                  fieldType="standard"
                  placeholder="Account Holder"
                  isDark
                />

                {values.accountHolderType === ACCOUNT_HOLDER_TYPE.PERSONAL && (
                  <Field
                    name="identityType"
                    type="text"
                    items={IDENTITY_TYPE_LIST}
                    getItemDisplayValue={(item: Option) => item.label}
                    component={MiniAppFocusWrapper}
                    innerComponent={InputField}
                    className="dark"
                    fieldType="select"
                    placeholder="Id Type"
                    isDark
                  />
                )}

                {values?.accountHolderType &&
                values.accountHolderType === ACCOUNT_HOLDER_TYPE.PERSONAL ? (
                  <Field
                    name="idNumber"
                    type="tel"
                    mask={getIdMask(values)}
                    placeholder={getIdPlaceholder(values.identityType.value)}
                    component={MiniAppFocusWrapper}
                    innerComponent={InputField}
                    className="dark"
                    fieldType="masked"
                    guide={!inMiniApp}
                    isDark
                  />
                ) : (
                  <Field
                    name="businessRegistrationNumber"
                    type="text"
                    mask={businessRegistrationMask}
                    placeholder="Business registration number"
                    component={MiniAppFocusWrapper}
                    innerComponent={InputField}
                    className="dark"
                    fieldType="masked"
                    guide={!inMiniApp}
                    isDark
                  />
                )}

                <Field
                  name="accountNumber"
                  type="tel"
                  maxLength={16}
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  className="dark"
                  fieldType="standard"
                  placeholder="Account Number"
                  isDark
                />
                <ShowIf condition={!changeBankDetailsDisabled && bavDisabled}>
                  <RevampFileUpload
                    name="proofOfBanking"
                    title="Proof of banking"
                    subtext="A clear copy of your Bank Account Confirmation Letter or Bank statement in PDF or JPEG not older than 3 months."
                    maxFileSizeText="Maximum size: 1MB"
                    acceptedFileTypes={[AcceptedFileTypes.pdf, AcceptedFileTypes.jpeg]}
                    maxFiles={6}
                    maxSize={1_024_000}
                    setFileTypes={setFileTypes}
                    stripPrefix
                  />
                </ShowIf>
              </>
            ) : (
              <>
                <section className="flex flex-col w-full">
                  <h3 className="text-white-50">Account type</h3>
                  <p className="text-grey-400">
                    {pendingUpdate ? 'Pending' : accType?.label ?? 'Loading...'}
                  </p>
                </section>
                <section className="flex flex-col w-full">
                  <h3 className="text-white-50">Bank</h3>
                  <p className="text-grey-400">
                    {pendingUpdate ? 'Pending' : bankName?.label ?? 'Loading...'}
                  </p>
                </section>
                <section className="flex flex-col w-full">
                  <h3 className="text-white-50">Branch name</h3>
                  <p className="text-grey-400">
                    {pendingUpdate ? 'Pending' : merchantBankDetails?.bankBranchName ?? 'Loading..'}
                  </p>
                </section>
                <section className="flex flex-col w-full">
                  <h3 className="text-white-50">Account holder</h3>
                  <p className="text-grey-400">
                    {pendingUpdate
                      ? 'Pending'
                      : merchantBankDetails?.bankAccountHolderName ?? 'Loading...'}
                  </p>
                </section>
                <section className="flex flex-col w-full">
                  <h3 className="text-white-50">Account number</h3>
                  <p className="text-grey-400">
                    {pendingUpdate
                      ? 'Pending'
                      : merchantBankDetails?.bankAccountNumber ?? 'Loading...'}
                  </p>
                </section>
                <ShowIf condition={!changeBankDetailsDisabled && bavDisabled}>
                  <p className="w-full max-w-lg text-grey-400">
                    Upload a clear copy of your Bank Account Confirmation Letter or Bank statement
                    in PDF or JPEG not older than 3 months.
                  </p>
                </ShowIf>
              </>
            )}
            <ShowIf condition={!changeBankDetailsDisabled}>
              <div className="flex w-full mt-6 gap-x-2">
                <button
                  type={changeBankDetails ? 'reset' : 'button'}
                  className="w-full h-12 p-2 border-2 border-white border-solid max-w-26 rounded-40 disabled:bg-grey-700 disabled:border-none disabled:cursor-not-allowed"
                  onClick={onClick}
                  disabled={isBankDetailsLoading}
                >
                  {changeBankDetails ? 'Cancel' : 'Change'}
                </button>
                <button
                  type="submit"
                  className="w-full h-12 p-2 bg-red-500 border-2 border-red-500 border-solid max-w-26 rounded-40 disabled:bg-grey-700 disabled:border-none disabled:cursor-not-allowed"
                  disabled={!bankDetailsValid || BAVAttempts === MAX_BAV_ATTEMPTS}
                >
                  Save
                </button>
              </div>
            </ShowIf>
          </Form>
        )}
      </Formik>
      {showOtp && <BankingDetailsOTP onRequestNewOTP={requestNewOTP} onExit={handleExit} />}
      <FullPageLoader showLoader={showLoader} />
      <UpdateInProgress show={showUpdateInProgress} setShow={setShowUpdateInProgress} />
    </>
  );
}

export default BankingDetailsForm;
